import {
  Building2,
  MapPin,
  Palette,
  Paintbrush,
  Users,
  KeyRound,
  Layers,
  Boxes,
  Plug,
  CreditCard,
  Sparkles,
  CalendarDays,
} from 'lucide-react'

/**
 * Roles that can see and use the Settings section today. Mirrors
 * `selectCanManage` (`@/features/auth/authSlice`) — the same gate the
 * sidebar has always used to decide whether "Settings" shows up at all.
 * Carried on every item so a future per-item gate has somewhere to read
 * from; nothing currently narrows below this list on a per-item basis.
 */
export const SETTINGS_ROLES = ['administrator', 'owner', 'global_super_admin']

/**
 * @typedef {Object} SettingsNavItem
 * @property {string} path - Route path; must match a route registered under
 *   `/settings` in `src/routes/index.jsx`.
 * @property {string} label
 * @property {import('lucide-react').LucideIcon} icon
 * @property {string} icon3d - The Fluent 3D icon name (components/icons3d), drawn at render time
 * @property {string} description - One line of context for the item.
 * @property {string[]} [roles] - Roles allowed to see this item. Not yet
 *   read anywhere: gating today is a single `canManage` check for the whole
 *   Settings section, so every item currently carries the same
 *   `SETTINGS_ROLES` value.
 */

/**
 * @typedef {Object} SettingsNavGroup
 * @property {'Workspace'|'Access'|'Data & connections'|'Billing'} group
 * @property {SettingsNavItem[]} items
 */

/**
 * Shared by the settings home, route tabs, page headings, and global
 * search so labels, descriptions, and destinations stay consistent.
 *
 * @type {SettingsNavGroup[]}
 */
export const settingsNav = [
  {
    group: 'Workspace',
    items: [
      {
        path: '/settings/organization',
        label: 'Organization',
        icon: Building2,
        icon3d: 'organization',
        description: "Manage your organization's information and locale preferences",
        roles: SETTINGS_ROLES,
      },
      {
        path: '/settings/branding',
        label: 'Branding & appearance',
        icon: Paintbrush,
        icon3d: 'branding',
        description: 'Personalize your workspace name, logos, and color scheme',
        roles: SETTINGS_ROLES,
      },
      {
        path: '/settings/campuses',
        label: 'Campuses',
        icon: MapPin,
        icon3d: 'location',
        description: 'Manage campus locations and timezone settings',
        roles: SETTINGS_ROLES,
      },
      {
        path: '/settings/chart-colors',
        label: 'Chart colors',
        icon: Palette,
        icon3d: 'palette',
        description: 'Customize the color palette used across charts',
        roles: SETTINGS_ROLES,
      },
    ],
  },
  {
    group: 'Access',
    items: [
      {
        path: '/settings/users',
        label: 'Users & roles',
        icon: Users,
        icon3d: 'people',
        description: 'Invite users and manage their roles and campus access',
        roles: SETTINGS_ROLES,
      },
      {
        path: '/settings/authentication',
        label: 'Authentication',
        icon: KeyRound,
        icon3d: 'key',
        description: 'Configure sign-in methods and login providers',
        roles: SETTINGS_ROLES,
      },
    ],
  },
  {
    group: 'Data & connections',
    items: [
      {
        path: '/settings/categories',
        label: 'Categories',
        icon: Layers,
        icon3d: 'categories',
        description: 'Organize your metrics into logical groups',
        roles: SETTINGS_ROLES,
      },
      {
        path: '/settings/partitions',
        label: 'Partition types',
        icon: Boxes,
        icon3d: 'partitions',
        description: 'Define dimensions for categorizing your metric data',
        roles: SETTINGS_ROLES,
      },
      {
        path: '/settings/integrations',
        label: 'Integrations',
        icon: Plug,
        icon3d: 'integrations',
        description: 'Connect your tools and keep data in sync',
        roles: SETTINGS_ROLES,
      },
      {
        path: '/settings/ai',
        label: 'AI assistant',
        icon: Sparkles,
        icon3d: 'assistant',
        description: 'Set the assistant persona and weekly brief delivery',
        roles: SETTINGS_ROLES,
      },
      {
        path: '/settings/annotations',
        label: 'Annotations',
        icon: CalendarDays,
        icon3d: 'annotations',
        description: 'Mark events, holidays, and campaigns on your charts',
        roles: SETTINGS_ROLES,
      },
    ],
  },
  {
    group: 'Billing',
    items: [
      {
        path: '/settings/subscription',
        label: 'Subscription & billing',
        icon: CreditCard,
        icon3d: 'billing',
        description: 'Manage your plan, usage, and billing details',
        roles: SETTINGS_ROLES,
      },
    ],
  },
]

/** Flat list of every settings nav item, in manifest order. */
export const settingsNavItems = settingsNav.flatMap((navGroup) => navGroup.items)

/**
 * The manifest is the single source of truth for a settings page's
 * `PageHeader` title and description too (FINAL fix round, finding 3) - a
 * page used to hardcode its own copy of both, which drifted from the nav
 * label ("Users & Roles" in the sidebar vs "Users & roles" on the page).
 * Pages read their `{ label, description }` from here instead of repeating
 * the strings.
 *
 * @param {string} path - Exact `settingsNav` item path, e.g.
 *   `/settings/organization`.
 * @returns {SettingsNavItem}
 * @throws {Error} if `path` isn't in the manifest - a page pointed at a
 *   path that doesn't exist is a bug, not a state to render around.
 */
export function getSettingsNavItem(path) {
  const item = settingsNavItems.find((navItem) => navItem.path === path)
  if (!item) {
    throw new Error(`getSettingsNavItem: no settingsNav item for path "${path}"`)
  }
  return item
}

export default settingsNav
